/**
 * Saved reports library: every saved view the workspace can see, filed into
 * folders. Folders come from each view's report config rather than a dedicated
 * table, so they persist through the existing saved-view endpoints.
 */
import { useMemo, useState } from 'react'
import {
  ChevronDown,
  ChevronRight,
  FolderOpen,
  FolderPlus,
  Loader2,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Star,
} from 'lucide-react'
import clsx from 'clsx'
import type { SavedView } from './types'
import { normalizeReportConfig } from './types'

const UNFILED = 'Unfiled'

export function folderOf(view: SavedView): string {
  const folder = normalizeReportConfig(view.filters?.report).folder.trim()
  return folder || UNFILED
}

export function SavedReportsPanel({
  views,
  activeViewId,
  loading,
  error,
  dirty,
  onSelect,
  onSaveAsNew,
  saving,
  collapsed: railCollapsed,
  onToggleCollapsed,
}: {
  views: SavedView[]
  activeViewId: string
  loading: boolean
  error: boolean
  /** Unsaved design edits exist — switching reports would discard them. */
  dirty: boolean
  onSelect: (view: SavedView) => void
  onSaveAsNew: (name: string, folder: string) => Promise<void>
  saving: boolean
  collapsed: boolean
  onToggleCollapsed: () => void
}) {
  const [query, setQuery] = useState('')
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newFolder, setNewFolder] = useState('')

  const folders = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const buckets = new Map<string, SavedView[]>()
    for (const view of views) {
      if (needle && !view.name.toLowerCase().includes(needle)) continue
      const key = folderOf(view)
      const bucket = buckets.get(key)
      if (bucket) bucket.push(view)
      else buckets.set(key, [view])
    }
    // Named folders first, alphabetical; Unfiled always last.
    return [...buckets.entries()]
      .sort((a, b) => {
        if (a[0] === UNFILED) return 1
        if (b[0] === UNFILED) return -1
        return a[0].localeCompare(b[0])
      })
      .map(([name, items]) => ({
        name,
        items: [...items].sort((a, b) => a.name.localeCompare(b.name)),
      }))
  }, [views, query])

  const existingFolders = useMemo(
    () => [...new Set(views.map(folderOf).filter((name) => name !== UNFILED))].sort(),
    [views],
  )

  const submitNew = async () => {
    if (!newName.trim()) return
    try {
      await onSaveAsNew(newName.trim(), newFolder.trim())
    } catch {
      // The Studio shows why the server refused it; the form stays open to try again.
      return
    }
    setCreating(false)
    setNewName('')
    setNewFolder('')
  }

  if (railCollapsed) {
    return (
      <div className="flex h-full min-h-0 w-9 shrink-0 flex-col items-center border-r border-white/[0.05] bg-black/20 py-2">
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="rounded p-1 text-stone-500 hover:bg-white/5 hover:text-amber-200"
          title="Show saved reports"
          aria-label="Show saved reports"
        >
          <PanelLeftOpen className="h-4 w-4" />
        </button>
        <span className="mt-3 text-[9px] uppercase tracking-[0.16em] text-stone-600 [writing-mode:vertical-rl]">
          Saved reports
        </span>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 w-[210px] shrink-0 flex-col overflow-hidden border-r border-white/[0.05] bg-black/20 2xl:w-[248px]">
      <div className="flex items-center justify-between gap-1 border-b border-white/[0.05] px-2 py-2">
        <span className="text-[9px] uppercase tracking-[0.14em] text-stone-500">Saved reports</span>
        <span className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setCreating((open) => !open)}
            disabled={error}
            className="inline-flex items-center gap-1 rounded border border-stone-600 px-1.5 py-0.5 text-[10px] text-stone-300 hover:border-amber-300/40 hover:text-amber-200 disabled:opacity-40"
            title={error ? 'Saved-report service unavailable' : 'Save the current design as a new report'}
          >
            <FolderPlus className="h-3 w-3" /> Save as
          </button>
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="rounded p-0.5 text-stone-500 hover:text-amber-200"
            title="Hide saved reports"
            aria-label="Hide saved reports"
          >
            <PanelLeftClose className="h-3.5 w-3.5" />
          </button>
        </span>
      </div>

      {creating ? (
        <div className="space-y-2 border-b border-white/[0.05] bg-amber-300/[0.03] px-3 py-2">
          <label className="block">
            <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">Report name</span>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Weekly debt pipeline"
              autoFocus
              className="mt-1 w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 outline-none [color-scheme:dark] focus:border-amber-300/40"
            />
          </label>
          <label className="block">
            <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">Folder</span>
            <input
              value={newFolder}
              onChange={(e) => setNewFolder(e.target.value)}
              list="report-folder-options"
              placeholder="Unfiled — or type a new folder"
              className="mt-1 w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 outline-none [color-scheme:dark] focus:border-amber-300/40"
            />
            <datalist id="report-folder-options">
              {existingFolders.map((folder) => (
                <option key={folder} value={folder} />
              ))}
            </datalist>
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!newName.trim() || saving}
              onClick={() => void submitNew()}
              className="inline-flex items-center gap-1.5 rounded bg-amber-300 px-2.5 py-1 text-[11px] font-medium text-black disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
              Create report
            </button>
            <button
              type="button"
              onClick={() => setCreating(false)}
              className="rounded border border-stone-600 px-2.5 py-1 text-[11px] text-stone-300"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      <div className="border-b border-white/[0.05] px-3 py-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-2 h-3 w-3 text-stone-600" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a report…"
            className="w-full rounded border border-stone-700 bg-black/40 py-1.5 pl-7 pr-2 text-[11px] text-stone-100 outline-none [color-scheme:dark] focus:border-amber-300/40"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {loading ? (
          <div className="flex items-center gap-2 px-1 py-3 text-[11px] text-stone-500">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading saved reports…
          </div>
        ) : error ? (
          <div className="rounded border border-amber-300/20 bg-amber-300/[0.04] px-2 py-2 text-[11px] text-amber-100">
            Saved-report service unavailable. The design tools and exports still work; this session
            cannot list or store reports until it responds.
          </div>
        ) : folders.length === 0 ? (
          <div className="px-1 py-3 text-[11px] text-stone-600">
            {query.trim() ? `No reports match “${query.trim()}”.` : 'No saved reports yet.'}
          </div>
        ) : (
          folders.map((folder) => {
            const isCollapsed = collapsed[folder.name] ?? false
            return (
              <div key={folder.name} className="mb-1">
                <button
                  type="button"
                  onClick={() =>
                    setCollapsed((current) => ({ ...current, [folder.name]: !isCollapsed }))
                  }
                  className="flex w-full items-center gap-1 rounded px-1 py-1 text-left text-[10px] uppercase tracking-[0.1em] text-stone-500 hover:text-amber-200"
                >
                  {isCollapsed ? (
                    <ChevronRight className="h-3 w-3 shrink-0" />
                  ) : (
                    <ChevronDown className="h-3 w-3 shrink-0" />
                  )}
                  <FolderOpen className="h-3 w-3 shrink-0 text-amber-200/60" />
                  <span className="truncate">{folder.name}</span>
                  <span className="ml-auto font-mono text-stone-600">{folder.items.length}</span>
                </button>
                {!isCollapsed ? (
                  <div className="ml-1 space-y-0.5 border-l border-white/[0.05] pl-2">
                    {folder.items.map((view) => {
                      const active = view.id === activeViewId
                      return (
                        <button
                          key={view.id}
                          type="button"
                          onClick={() => {
                            if (active) return
                            if (
                              dirty &&
                              !window.confirm(
                                'Discard the unsaved changes to this report design and open the selected report?',
                              )
                            ) {
                              return
                            }
                            onSelect(view)
                          }}
                          className={clsx(
                            'block w-full rounded px-2 py-1.5 text-left transition',
                            active ? 'bg-amber-300/[0.10] text-amber-100' : 'text-stone-300 hover:bg-white/[0.03]',
                          )}
                        >
                          <span className="flex items-center gap-1">
                            {view.isSystem ? (
                              <Star className="h-2.5 w-2.5 shrink-0 text-amber-200/70" />
                            ) : null}
                            <span className="truncate text-[12px]">{view.name}</span>
                          </span>
                          <span className="block text-[9px] text-stone-600">
                            v{view.version}
                            {view.versions?.length
                              ? ` · ${view.versions.length} version${view.versions.length === 1 ? '' : 's'}`
                              : ''}
                            {' · '}
                            {String(view.status).toLowerCase()}
                            {view.isShared ? ' · shared' : ''}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                ) : null}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
